import React, { useContext, useEffect, useState } from "react";
import { Region } from "../shared/interfaces/Region";
import DataTableSkeleton from "../shared/DataTableSkeleton";
import {
  DataTable,
  DataTableMultiSortMetaType,
  DataTableSortOrderType,
  DataTableSortParams,
} from "primereact/datatable";
import { Column } from "primereact/column";
import { TextTemplate } from "../shared/Grid/BodyTemplates";
import { Snackbar } from "../shared/Snackbar";
import { Alert, AlertColor, Button, ButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Col, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GlobalFilter } from "../shared/Grid/GlobalFilterTemplate";
import RefreshLoadingButton from "../shared/RefreshLoadingButton";
import { AuthContext } from "../contexts/AuthContext";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import { useSiteDelete } from "../data/Settings/useSiteDelete";
import { useAlternativeRegionNames } from "../data/Settings/useAlternativeRegionNames";
import { LOCALSTORAGE_KEYS } from "../shared/constants";

interface Props {
  customerSelected: boolean;
  customerId?: number;
  sitesData?: Region[];
  sitesDataLoading: boolean;
  sitesDataFetching: boolean;
  onDataRefreshClick: () => void;
  onNewSiteClick: () => void;
  refreshSiteData: () => void;
}

const sortFieldStorageKey = LOCALSTORAGE_KEYS.sitesSortField;
const sortOrderStorageKey = LOCALSTORAGE_KEYS.sitesSortOrder;

export function SiteGrid(props: Props) {
  const { t } = useTranslation();

  const { customerId } = props;

  const authContext = useContext(AuthContext);
  const userCanAddSite = authContext.canI("add", "sites");
  const userCanDeleteSite = authContext.canI("delete", "sites");

  const altRegionNames = useAlternativeRegionNames(customerId);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<AlertColor>("success");

  const siteDelete = useSiteDelete({
    onSuccess: onDeleteSiteSuccess,
    onError: onDeleteSiteError,
  });

  const [globalFilter, setGlobalFilter] = useState<string>("");

  function handleGlobalFilterInput(event: React.FormEvent<HTMLInputElement>): void {
    const element = event.target as HTMLInputElement;
    setGlobalFilter(element.value);
  }

  const [multiSortMeta, setMultiSortMeta] = useState<DataTableMultiSortMetaType>();

  const onSort = (e: DataTableSortParams) => {
    const field = e.multiSortMeta?.at(0)?.field as string;
    const order = e.multiSortMeta?.at(0)?.order;
    localStorage.setItem(sortFieldStorageKey, field);
    order && localStorage.setItem(sortOrderStorageKey, order?.toString());
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  };

  useEffect(() => {
    const field = localStorage.getItem(sortFieldStorageKey) as string;
    const order = localStorage.getItem(sortOrderStorageKey) as DataTableSortOrderType;
    const sort: DataTableMultiSortMetaType = [{ field: field, order: order }];
    setMultiSortMeta(sort);
  }, [props]);

  function handleConfirmDeleteSiteClick(siteId: number) {
    userCanDeleteSite && siteDelete.mutate(siteId);
  }

  function onDeleteSiteSuccess() {
    setSnackbarText(
      t("settings.siteDeleted", {
        val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
      }),
    );
    setSnackbarSeverity("success");
    setShowSnackbar(true);

    props.refreshSiteData();
  }

  function onDeleteSiteError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: altRegionNames.data?.siteAltName ?? "$t(common.site)",
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    } else {
      setSnackbarText(
        t("common.dataDeleteError", {
          val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
        }),
      );
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  }

  const handleErrorSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const headerToolbar = (
    <Row>
      <Col>
        {props.customerSelected && userCanAddSite && (
          <Button
            variant="contained"
            color="primary"
            onClick={props.onNewSiteClick}
            startIcon={<FontAwesomeIcon style={{ cursor: "pointer" }} icon={faPlus} />}
          >
            {altRegionNames.data?.siteAltName ?? t("common.site")}
          </Button>
        )}
      </Col>
      <Col className="d-flex justify-content-center">
        <GlobalFilter onInput={handleGlobalFilterInput} />
      </Col>
      <Col className="d-flex justify-content-end">
        <RefreshLoadingButton isLoading={props.sitesDataFetching} onClick={props.onDataRefreshClick} />
      </Col>
    </Row>
  );

  const actionBodyTemplate = (rowData: Region) => {
    return (
      <ButtonGroup variant="outlined">
        {userCanDeleteSite && (
          <DeleteConfirmationButton
            confirmationDialogHeader={t("common.deleteConfirmationShort", {
              val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
              transform: "lowercase",
            })}
            confirmationDialogMessage={t("common.deleteConfirmation", {
              val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
              transform: "lowercase",
            })}
            onConfirmDeleteButtonClick={() => handleConfirmDeleteSiteClick(rowData.id)}
          />
        )}
      </ButtonGroup>
    );
  };

  if (props.sitesDataLoading) return <DataTableSkeleton />;
  else
    return (
      <React.Fragment>
        <div className="datatable-responsive">
          <DataTable
            value={props.sitesData}
            header={headerToolbar}
            // If the global filter is an empty string add a space, otherwise some results may get filtered out
            globalFilter={globalFilter === "" ? " " : globalFilter}
            filterDisplay="row"
            removableSort
            sortMode="multiple"
            className="p-datatable-responsive p-datatable-gridlines p-datatable-sm"
            emptyMessage={t("common.emptyMessage", {
              val: altRegionNames.data?.sitesAltName ?? "$t(common.sites)",
            })}
            loading={props.sitesDataLoading}
            multiSortMeta={multiSortMeta}
            onSort={onSort}
          >
            <Column
              field={"name"}
              header={t("settings.siteName", {
                val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
              })}
              body={(rowData: Region) => (
                <TextTemplate
                  rowData={rowData}
                  field={"name"}
                  header={t("settings.siteName", {
                    val: altRegionNames.data?.siteAltName ?? "$t(common.site)",
                  })}
                />
              )}
              filter
              showFilterMenu={false}
              showClearButton={false}
              sortable
              sortField="name"
            />
            {
              <Column
                body={actionBodyTemplate}
                headerStyle={{ width: "8em", textAlign: "center" }}
                bodyStyle={{ textAlign: "center", overflow: "visible" }}
                header={t("common.actions")}
              />
            }
          </DataTable>
        </div>

        <Snackbar open={showSnackbar} onClose={handleErrorSnackbarClose}>
          <Alert severity={snackbarSeverity} variant="filled">
            {snackbarText}
          </Alert>
        </Snackbar>
      </React.Fragment>
    );
}
